import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Link, useLocation } from 'react-router-dom';
import { statusOf, type ApplicantStatusView } from '@/components/domain/recruitment/applicantStatusModel';
import { threadApi } from './threadApi';
import { threadColor } from './threadStyles';
import { canOpenThread, type ThreadSummary } from './threadTypes';

export { canOpenThread };

/**
 * 대화방으로 들어가는 작은 부품 — 지원자 관리 목록·지원 내역·마이페이지가 같은 것을 쓴다.
 * - [UnreadCountBadge]: 안 읽은 수(1 이상일 때만, 99+), 청록 바탕 흰 글자 작게
 * - [ThreadEntryLink]: "대화방" 글 링크 + 배지 — 카드 전체가 눌리는 목록 안에서도 카드 클릭으로 번지지 않는다
 * - [ThreadListLink]: 머리 오른쪽 "대화 목록" + 오른쪽 화살표(SVG)
 * - [useThreadUnreadCounts]: 목록을 받은 뒤 한 번에 묻는다. 돌아오기·다시 보이기 때 다시 묻는다(옛 숫자가 남지 않게)
 */

export const unreadLabel = (count: number): string => (count > 99 ? '99+' : String(count));

const Badge = styled.span`
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: ${threadColor.accent};
    color: #fff;
    font-size: 11px;
    font-weight: 800;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
`;

export const UnreadCountBadge = ({ count, className }: { count: number | undefined; className?: string }) =>
    count && count > 0 ? (
        <Badge className={className} role="status" aria-label={`안 읽은 메시지 ${count}개`}>
            {unreadLabel(count)}
        </Badge>
    ) : null;

const EntryLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 32px;
    color: ${threadColor.primaryDeep};
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;
`;

export interface ThreadEntryLinkProps {
    applicationId: number | string;
    unread?: number;
    label?: string;
    className?: string;
}

export const ThreadEntryLink = ({ applicationId, unread, label = '대화방', className }: ThreadEntryLinkProps) => (
    <EntryLink
        className={className}
        to={`/applications/${applicationId}/thread`}
        // 카드 전체가 눌리는 목록(지원서 보기 등) 안에 있어도 카드 클릭으로 번지지 않게
        onClick={(event: React.MouseEvent) => event.stopPropagation()}
    >
        {label}
        <UnreadCountBadge count={unread} />
    </EntryLink>
);

/** 꺾쇠(SVG) — 대화방 링크는 부품 안에 아이콘을 넣을 수 없어 같은 모양(ChevronRightRounded)을 마스크로 그린다 */
const CHEVRON_MASK: string = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M9.29 6.71a1 1 0 0 0 0 1.41L13.17 12l-3.88 3.88a1 1 0 1 0 1.41 1.41l4.59-4.59a1 1 0 0 0 0-1.41L10.7 6.7a1 1 0 0 0-1.41.01z'/%3E%3C/svg%3E")`;

/** "대화방 ›" — 지원자 관리 줄·지원서 화면이 같은 모양(글자 + 16px 꺾쇠, 안 읽은 수 배지) */
export const ThreadChevronLink = styled(ThreadEntryLink)`
    &::after {
        content: '';
        display: block;
        flex: 0 0 16px;
        width: 16px;
        height: 16px;
        margin-left: -2px;
        background-color: currentColor;
        -webkit-mask: ${CHEVRON_MASK} center / 16px 16px no-repeat;
        mask: ${CHEVRON_MASK} center / 16px 16px no-repeat;
    }
`;

/** 오른쪽 화살표 — 글자(→) 대신 SVG(MUI ArrowForward 모양, UI 가이드 4.1). 글자색을 따라간다 */
const ARROW_FORWARD_SVG: string = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='m12 4-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z'/%3E%3C/svg%3E")`;

/** 누르는 높이 44(머리 줄 높이는 그대로 두려고 위아래 -6px — 옛 32px 자리) */
const ListLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    min-height: 44px;
    margin: -6px 0;
    color: ${threadColor.primaryDeep};
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
    white-space: nowrap;
    text-decoration: none;
    -webkit-tap-highlight-color: transparent;

    &::after {
        content: '';
        display: block;
        flex: none;
        width: 16px;
        height: 16px;
        background-color: currentColor;
        -webkit-mask: ${ARROW_FORWARD_SVG} center / contain no-repeat;
        mask: ${ARROW_FORWARD_SVG} center / contain no-repeat;
    }
`;

/** 화면 머리 오른쪽 "대화 목록" + 화살표 */
export const ThreadListLink = () => <ListLink to="/threads">대화 목록</ListLink>;

/**
 * 여러 지원 건의 안 읽은 수 — `{ 지원서ID: 수 }`. 실패하면 빈 값(배지만 안 보임).
 * 주소 이동 키(뒤로 와서 되살린 목록 포함)·화면이 다시 보일 때마다 새로 묻는다.
 */
export const useThreadUnreadCounts = (applicationIds: Array<number | string>): Record<string, number> => {
    const [counts, setCounts] = useState<Record<string, number>>({});
    const location = useLocation();
    const key: string = applicationIds.map(String).sort().join(',');
    const [visibleTick, setVisibleTick] = useState<number>(0);

    useEffect(() => {
        const onVisible = (): void => {
            if (document.visibilityState === 'visible') setVisibleTick((tick) => tick + 1);
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('pageshow', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('pageshow', onVisible);
        };
    }, []);

    useEffect(() => {
        if (!key) {
            setCounts({});
            return undefined;
        }
        let alive: boolean = true;
        void threadApi.unreadCounts(key.split(',')).then((next) => {
            if (alive) setCounts(next);
        });
        return () => {
            alive = false;
        };
    }, [key, location.key, visibleTick]);

    return counts;
};

/**
 * 여러 지원 건의 대화방 요약 — `{ 지원서ID: { stage, unread, disputedAt, disputeResolution } }`(대화방이 없는 건은 빠짐).
 * 지원 내역 상태 글자(대화방 단계 기준)와 안 읽은 수를 한 번에 받는다. 다시 묻는 때는 [useThreadUnreadCounts] 와 같다. 실패하면 빈 값.
 */
export const useThreadSummaries = (applicationIds: Array<number | string>): Record<string, ThreadSummary> => {
    const [summaries, setSummaries] = useState<Record<string, ThreadSummary>>({});
    const location = useLocation();
    const key: string = applicationIds.map(String).sort().join(',');
    const [visibleTick, setVisibleTick] = useState<number>(0);

    useEffect(() => {
        const onVisible = (): void => {
            if (document.visibilityState === 'visible') setVisibleTick((tick) => tick + 1);
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('pageshow', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('pageshow', onVisible);
        };
    }, []);

    useEffect(() => {
        if (!key) {
            setSummaries({});
            return undefined;
        }
        let alive: boolean = true;
        void threadApi.summaries(key.split(',')).then((next) => {
            if (alive) setSummaries(next);
        });
        return () => {
            alive = false;
        };
    }, [key, location.key, visibleTick]);

    return summaries;
};

/**
 * 지원 내역(지원자) 상태 — 대화방 단계 기준(노쇼·이의 제기됨·노쇼 취소됨·면접 불참·근무 확정 …), 대화방이 없거나 지원완료 단계면 지원서 상태.
 * 글자·색 갈래는 치과 지원자 관리와 같은 applicantStatusModel.statusOf 하나를 쓴다.
 */
export const applicationStatusOf = (applyStatus: string, summary?: ThreadSummary | null): ApplicantStatusView =>
    // 취소 건은 대화방에서 입사 포기(WITHDRAWN)한 것만 단계로 보이고, 그 밖은 지원서 상태("취소")
    summary && (applyStatus !== 'CANCEL' || summary.stage === 'WITHDRAWN') ? statusOf(summary.stage, applyStatus, !!summary.disputedAt, summary.disputeResolution ?? null, summary.offerKind ?? null) : statusOf(null, applyStatus);

/** 메뉴 아이콘 오른쪽 위에 얹는 안 읽은 수(마이페이지 "대화" 칸) — 메뉴 칸의 `span` 글자 규칙에 걸리지 않게 b 로 그린다 */
const IconWrap = styled.div`
    position: relative;
    display: inline-flex;

    > b {
        position: absolute;
        top: -6px;
        left: calc(100% - 6px);
    }
`;

export const IconWithUnread = ({ icon, count }: { icon: React.ReactNode; count: number }) => (
    <IconWrap>
        {icon}
        {count > 0 && (
            <Badge as="b" role="status" aria-label={`안 읽은 메시지 ${count}개`}>
                {unreadLabel(count)}
            </Badge>
        )}
    </IconWrap>
);

/** 내 대화방 안 읽은 수 합계(서버 집계, 개수 제한 없음) — 마이페이지·메뉴 배지. 실패하면 0 */
export const useThreadUnreadTotal = (enabled: boolean = true): number => {
    const [total, setTotal] = useState<number>(0);
    const location = useLocation();
    const [visibleTick, setVisibleTick] = useState<number>(0);
    useEffect(() => {
        const onVisible = (): void => {
            if (document.visibilityState === 'visible') setVisibleTick((tick) => tick + 1);
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, []);
    useEffect(() => {
        if (!enabled) {
            setTotal(0);
            return undefined;
        }
        let alive: boolean = true;
        void threadApi.unreadTotal().then((next: number) => {
            if (alive) setTotal(next);
        });
        return () => {
            alive = false;
        };
    }, [enabled, location.key, visibleTick]);
    return total;
};
